import { FunctionComponent, useEffect, useRef, useState } from "react";
import { t } from "i18next";

import { TooltipWrapper } from "../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { translationKeys } from "../../../models/app-translation-keys";

import "./ImageCountControl.scss";

import { observer } from "mobx-react-lite";

import { InputNumber } from "primereact/inputnumber";
import { classNames } from "primereact/utils";

import { faThLarge } from "@fortawesome/pro-regular-svg-icons";

import { ActionButton } from "../../../components/Shared";
import { useStore } from "../../../stores/StoreContext";

interface IImageCountControlProps {
  imageCount: number;
  disabled: boolean;
  onChange: (imageCount: number) => void; // called whenever local imageCount changes
}

const minImageCount = 1;
const maxImageCount = 10;

export const ImageCountControl: FunctionComponent<IImageCountControlProps> = observer(
  ({ imageCount, disabled, onChange }) => {
    const [showImageCountInput, setShowImageCountInput] = useState<boolean>(false);

    const containerRef = useRef<HTMLDivElement | null>(null);

    const { globalErrorsStore } = useStore();

    const isImageCountValid = imageCount >= minImageCount && imageCount <= maxImageCount;

    const featureTranslationKeys = translationKeys.feature.imageGeneration;

    useEffect(() => {
      if (!isImageCountValid) {
        globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.invalidImageCount, {
          min: minImageCount,
          max: maxImageCount,
        });
        console.error("[ImageCountControl] Invalid image image count");
      } else {
        globalErrorsStore.removeError(featureTranslationKeys.errors.invalidImageCount);
      }
    }, [imageCount]);

    const handleBlur: React.FocusEventHandler<HTMLDivElement> = e => {
      const next = e.relatedTarget as HTMLElement | null;

      // if focus moves outside the container, close the input
      if (!next || !containerRef.current?.contains(next)) {
        setShowImageCountInput(false);
      }
    };

    return (
      <div
        ref={containerRef}
        className={classNames("image-count-button-toggle", { "image-count-toggle": showImageCountInput })}
        onBlur={handleBlur}
        data-testid="image-count-control"
      >
        <TooltipWrapper
          targetRef=".image-count-button-toggle"
          position="top"
          content={t(featureTranslationKeys.tooltips.imageCountToggleTooltip)}
          disabled={disabled}
        />

        <ActionButton
          icon={faThLarge}
          id="image-count-toggle-button"
          hideBackground={true}
          buttonDisabled={disabled}
          onClick={() => setShowImageCountInput(s => !s)}
          label={!showImageCountInput ? `${imageCount}` : ""}
        />

        {showImageCountInput && (
          <div className="image-count-input">
            <InputNumber
              id="imagegen-count"
              data-testid="image-count-input"
              value={imageCount}
              onValueChange={e => onChange(e.value ?? imageCount)}
              showButtons
              min={minImageCount}
              max={maxImageCount}
              step={1}
              disabled={disabled}
              inputClassName={!isImageCountValid ? "p-invalid" : undefined}
              aria-label={t(featureTranslationKeys.imageCountToggleAria)}
            />
          </div>
        )}
      </div>
    );
  },
);
